import { Badge, Box, Flex, Text } from '@chakra-ui/react';
import { translationAction } from 'actions';
import { setOutputLanguage } from 'actions/translation';
import { ActionMeta, ChakraStylesConfig, MultiValue, Select } from 'chakra-react-select';
import { TRANSLATED_FILE_STATUS } from 'helpers/enum';
import { RootState } from 'helpers/store';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

type Option = { label: string; value: string };

/* DS combobox styling — maps chakra-react-select to DS tokens (design-guidelines.md §4 Input).
   Uses CSS vars for colors so light/dark follow Chakra semantic tokens. */
const dsSelectStyles: ChakraStylesConfig<any, boolean> = {
  control: (provided, state) => ({
    ...provided,
    bg: 'bg.surface',
    borderRadius: 'md',
    borderWidth: '1px',
    borderColor: state.isFocused ? 'primary.solid' : 'border.strong',
    boxShadow: state.isFocused ? '0 0 0 3px var(--chakra-colors-primary-soft)' : 'none',
    minH: '40px',
    _hover: { borderColor: 'primary.solid' },
  }),
  placeholder: (provided) => ({ ...provided, color: 'text.subtle', fontSize: 'sm' }),
  menuList: (provided) => ({
    ...provided,
    bg: 'bg.surfaceRaised',
    borderWidth: '1px',
    borderColor: 'border.default',
    borderRadius: 'md',
    boxShadow: 'ds_lg',
    py: 1,
  }),
  option: (provided, state) => ({
    ...provided,
    fontSize: 'sm',
    color: state.isSelected ? 'primary.solid' : 'text.default',
    bg: state.isSelected ? 'primary.soft' : 'transparent',
    _hover: { bg: 'primary.soft', color: 'primary.solid' },
  }),
  multiValue: (provided) => ({
    ...provided,
    bg: 'primary.soft',
    color: 'primary.solid',
    borderRadius: 'sm',
  }),
  multiValueLabel: (provided) => ({ ...provided, color: 'primary.solid', fontSize: 'xs' }),
  dropdownIndicator: (provided) => ({ ...provided, bg: 'transparent', color: 'text.subtle' }),
  singleValue: (provided) => ({ ...provided, color: 'text.default', fontSize: 'sm' }),
  input: (provided) => ({ ...provided, color: 'text.default' }),
};

const LanguagesMode = () => {
  const { t } = useTranslation();

  const dispatch = useDispatch();
  const { data: languages } = useSelector((store: RootState) => store.language);
  const { current } = useSelector((store: RootState) => store.translation);
  const isTranslating = useSelector(
    (state) =>
      state.translation.current.results?.some(
        (file) => file.status === TRANSLATED_FILE_STATUS.PENDING
      )
  );

  const outputLanguageSelections = current?.output_languages.map((lang) => {
    return {
      label: lang.name,
      value: lang.code,
    };
  });

  const languageOptions = useMemo(() => {
    return languages?.map(({ code, name }) => ({ label: name, value: code }));
  }, [languages]);

  const translateFromOptions = [
    { label: t('translator.sourceLanguageLabel'), value: '' },
    ...languageOptions,
  ];

  const setInputLanguage = (option: Option | null, _actionMeta: ActionMeta<Option>) => {
    if (option) {
      dispatch(translationAction.setInputLanguage([{ code: option.value, name: option.label }]));
    }
  };

  const handleSelectChange = (
    e: MultiValue<{
      label: string;
      value: string;
    }>
  ) => {
    // setOutputLanguageSelections([...e]);
    const outputLanguages = e.map((lang: { label: string; value: string }) => {
      return {
        code: lang.value,
        name: lang.label,
      };
    });

    dispatch(setOutputLanguage(outputLanguages));
  };

  useEffect(() => {
    dispatch(translationAction.fetchAllLanguageData());
  }, []);

  return (
    /* DS languages pane — surface, border, ds_sm shadow (design-guidelines.md §4, SC-03) */
    <Flex
      direction='column'
      overflow='hidden'
      h={'100%'}
      minH={0}
      bg='bg.surface'
      borderWidth='1px'
      borderColor='border.default'
      borderRadius='lg'
      shadow='ds_sm'
    >
      {/* Header */}
      <Box flexShrink={0} px={4} pt={4} pb={3}>
        <Text color='text.default' fontFamily='heading' fontSize='md' fontWeight={600}>
          {t('translator.languages')}
        </Text>
        <Text mt={2} color='text.muted' fontSize='sm'>
          {t('translator.languageInstructions')}
        </Text>
      </Box>

      <Flex direction='column' flex={1} gap={6} overflowY='auto' px={4} pb={4}>
        {/* Source language */}
        <Box>
          <Text mb={2} color='text.default' fontSize='sm' fontWeight={500}>
            {t('translator.translateFrom')}
          </Text>
          <Select<Option, false>
            chakraStyles={dsSelectStyles}
            value={
              translateFromOptions?.find((e) => current?.input_languages?.[0]?.code === e.value) ??
              translateFromOptions[0]
            }
            name='translation-input'
            options={translateFromOptions}
            onChange={setInputLanguage}
            placeholder={`${translateFromOptions[0].label}`}
            isDisabled={isTranslating}
          ></Select>
        </Box>

        {/* Target languages — 100+ searchable, multi (max 5) */}
        <Box>
          <Flex align='center' justify='space-between' mb={2}>
            <Text color='text.default' fontSize='sm' fontWeight={500}>
              {t('translator.targetLanguageLabel')}
            </Text>
            <Badge variant='ds-primary'>
              {t('translator.selectedCount', { count: outputLanguageSelections.length })}
            </Badge>
          </Flex>
          <Select<Option, true>
            chakraStyles={dsSelectStyles}
            menuPlacement='auto'
            placeholder={t('translator.targetLanguagePlaceholder')}
            options={languageOptions}
            isMulti
            onChange={handleSelectChange}
            value={outputLanguageSelections}
            isOptionDisabled={(_option: Option) => outputLanguageSelections.length >= 5}
            isDisabled={isTranslating}
          />
          <Text mt={2} color='text.muted' fontSize='xs'>
            {t('translator.targetLanguageHint', { count: languageOptions?.length ?? 0 })}
          </Text>
        </Box>
      </Flex>
    </Flex>
  );
};

export default LanguagesMode;
